import type { CourseModule } from '@/types/lesson';

/**
 * Модуль 4: Переписывание истории
 */
const module4: CourseModule = {
  id: 'module-4',
  title: 'Переписывание истории',
  slug: 'rewriting-history',
  order: 4,
  lessons: [
    {
      id: '4-1',
      moduleId: 'module-4',
      title: 'Интерактивный rebase',
      slug: 'interactive-rebase',
      order: 1,
      theory: `# Интерактивный rebase

Интерактивный rebase — мощный инструмент для изменения истории коммитов.

:::diagram rebase-flow:::

## История концепции rebase

### Эволюция подходов к изменению истории:

| Система | Подход | Возможности |
|---------|--------|-------------|
| CVS | Нет изменения истории | Только новые коммиты |
| Subversion | svn merge --reverse | Сложно, опасно |
| **Git** | **Rebase, filter-branch** | **Гибко, мощно** |

### Почему rebase появился в Git?

В распределённых системах каждый разработчик имеет полную копию истории. Это даёт свободу экспериментировать локально:

- **Централизованные VCS**: История на сервере — нельзя менять
- **Git**: Локальная история — можно переписать до отправки

Linus Torvalds создал rebase для:
1. Очистки истории перед отправкой
2. Перемещения веток на новую базу
3. Объединения и редактирования коммитов

## Что такое rebase?

Rebase перемещает ветку на новую базу, переписывая историю.

### Обычный rebase:

\`\`\`
ДО rebase:
main:      A --- B --- C
                 \\
feature:         D --- E

ПОСЛЕ git rebase main:
main:      A --- B --- C
                       \\
feature:               D' --- E'
\`\`\`

Коммиты D и E были пересозданы на новой базе C.

### Интерактивный режим:

\`\`\`bash
# Изменить последние 3 коммита
git rebase -i HEAD~3

# Или относительно другой ветки
git rebase -i main
\`\`\`

## Глубокое погружение: Как работает rebase

### Алгоритм rebase:

\`\`\`
1. Сохранить список коммитов ветки
   git log --oneline base..HEAD

2. Сбросить ветку на базу
   git reset --hard base

3. Применить каждый коммит заново
   for commit in commits:
     git cherry-pick commit

4. Для интерактивного — применить команды:
   pick, squash, reword, edit, drop
\`\`\`

### Что происходит с коммитами:

\`\`\`bash
# Исходные коммиты
A (a1b2c3d) Добавить функцию
B (e4f5g6h) Исправить опечатку
C (i7j8k9l) Добавить тесты

# После rebase - новые хеши!
A' (m1n2o3p) Добавить функцию    # Новый хеш!
B' (q4r5s6t) Исправить опечатку  # Новый хеш!
C' (u7v8w9x) Добавить тесты      # Новый хеш!
\`\`\`

**Важно**: Rebase создаёт новые коммиты с новыми хешами!

### Структура файла todo при интерактивном rebase:

\`\`\`bash
# Открывается в редакторе:
pick a1b2c3d Добавить функцию входа
squash e4f5g6h Исправить опечатку
reword i7j8k9l Добавить валидацию
drop m0n1o2p Удалить отладочный код

# Комментарии:
# Commands:
# p, pick <commit> = use commit
# r, reword <commit> = use commit, but edit the commit message
# e, edit <commit> = use commit, but stop for amending
# s, squash <commit> = use commit, but meld into previous commit
# f, fixup [-C | -c] <commit> = like "squash" but keep only the previous
# x, exec <command> = run command (the rest of the line) using shell
# b, break = stop here (continue rebase later with 'git rebase --continue')
# d, drop <commit> = remove commit
# l, label <label> = label current HEAD with a name
# t, reset <label> = reset HEAD to a label
\`\`\`

## Доступные действия

### pick (p) — оставить коммит

\`\`\`bash
pick a1b2c3d Добавить функцию
# Коммит остаётся без изменений
\`\`\`

### reword (r) — изменить сообщение

\`\`\`bash
reword a1b2c3d Добавить функцию
# Откроется редактор для нового сообщения
\`\`\`

Используется для:
- Исправления опечаток в сообщении
- Добавления деталей
- Приведения к стандарту (Conventional Commits)

### edit (e) — остановиться для изменения

\`\`\`bash
edit a1b2c3d Добавить функцию
# Git остановится на этом коммите
\`\`\`

Используется для:
- Разделения коммита на несколько
- Добавления изменений в коммит
- Изменения файлов

\`\`\`bash
# При остановке на edit:
git status
# Stopped at a1b2c3d... Добавить функцию

# Внести изменения
vim src/file.ts

# Добавить в текущий коммит
git add .
git commit --amend

# Продолжить rebase
git rebase --continue
\`\`\`

### squash (s) — объединить с предыдущим

\`\`\`bash
pick a1b2c3d Добавить функцию входа
squash e4f5g6h Исправить опечатку
# Объединит коммиты, откроет редактор для объединения сообщений
\`\`\`

Используется для:
- Объединения мелких "fixup" коммитов
- Создания атомарного коммита

### fixup (f) — объединить без сообщения

\`\`\`bash
pick a1b2c3d Добавить функцию входа
fixup e4f5g6h Исправить опечатку
# Объединит коммиты, отбросив сообщение второго
\`\`\`

Используется для:
- Быстрого объединения без редактирования сообщения
- Автоматического объединения с --autosquash

### drop (d) — удалить коммит

\`\`\`bash
drop a1b2c3d Отладочный код
# Коммит будет удалён из истории
\`\`\`

Используется для:
- Удаления отладочного кода
- Удаления коммитов с чувствительными данными
- Очистки истории

## Автоматизация с autosquash

### Создание fixup коммитов:

\`\`\`bash
# Создать fixup коммит для конкретного коммита
git commit --fixup a1b2c3d

# Создать fixup коммит по части сообщения
git commit --fixup :/Добавить функцию

# Пример:
# a1b2c3d Добавить функцию входа
# git commit --fixup a1b2c3d создаст:
# fixup! Добавить функцию входа
\`\`\`

### Автоматическое объединение:

\`\`\`bash
# Rebase с автоматическим объединением fixup
git rebase -i --autosquash main

# Git автоматически расставит squash/fixup команды:
pick a1b2c3d Добавить функцию входа
fixup e4f5g6h fixup! Добавить функцию входа
\`\`\`

## Практические кейсы

### Кейс 1: "Очистка истории перед PR"

\`\`\`bash
# История с множеством мелких коммитов:
git log --oneline
# m9n8o7p Исправить опечатку
# l6k5j4i Ещё исправление
# k3h2g1f WIP: функция
# j0e9d8c Начать функцию
# b7a6s5d Добавить базовую структуру

# Интерактивный rebase
git rebase -i HEAD~5

# В редакторе:
pick b7a6s5d Добавить базовую структуру
squash j0e9d8c Начать функцию
squash k3h2g1f WIP: функция
squash l6k5j4i Ещё исправление
squash m9n8o7p Исправить опечатку

# Результат — один чистый коммит:
# a1b2c3d Добавить функцию аутентификации
\`\`\`

### Кейс 2: "Разделение коммита"

\`\`\`bash
# Коммит содержит две функции
git log --oneline -1
# a1b2c3d Добавить функцию A и B

# Разделить на два коммита
git rebase -i HEAD~1
# edit a1b2c3d Добавить функцию A и B

# Git остановится
git reset HEAD^

# Создать два коммита
git add feature-a/
git commit -m "Добавить функцию A"
git add feature-b/
git commit -m "Добавить функцию B"

# Продолжить
git rebase --continue
\`\`\`

### Кейс 3: "Изменение сообщения коммита"

\`\`\`bash
# Привести сообщения к Conventional Commits
git log --oneline -5
# m9n8o7p fix bug
# l6k5j4i add feature
# k3h2g1f update docs

git rebase -i HEAD~5

# В редакторе:
reword m9n8o7p fix bug
reword l6k5j4i add feature
reword k3h2g1f update docs

# Новые сообщения:
# m9n8o7p fix(auth): исправить валидацию пароля
# l6k5j4i feat(api): добавить endpoint для пользователей
# k3h2g1f docs: обновить README
\`\`\`

### Кейс 4: "Удаление коммита с секретом"

\`\`\`bash
# Случайно закоммитили API ключ
git log --oneline -3
# c3d4e5f Добавить API вызовы
# b2a3c4d Добавить конфиг с API_KEY  # ОШИБКА!
# a1b2c3d Начальный коммит

# Удалить коммит с секретом
git rebase -i HEAD~3

# В редакторе:
pick a1b2c3d Начальный коммит
drop b2a3c4d Добавить конфиг с API_KEY
pick c3d4e5f Добавить API вызовы

# ВАЖНО: Также нужно удалить секрет из файла!
\`\`\`

### Кейс 5: "Перемещение коммита"

\`\`\`bash
# Коммит в неправильной ветке
# Хотим переместить a1b2c3d в feature-branch

# В исходной ветке:
git rebase -i HEAD~3
# Удалить коммит (drop)

# В целевой ветке:
git checkout feature-branch
git cherry-pick a1b2c3d
\`\`\`

## Продвинутые техники

### Rebase с конфликтами:

\`\`\`bash
git rebase -i main
# CONFLICT (content): Merge conflict in src/file.ts

# Разрешить конфликт
vim src/file.ts
git add src/file.ts

# Продолжить rebase
git rebase --continue

# Или пропустить текущий коммит
git rebase --skip

# Или отменить rebase
git rebase --abort
\`\`\`

### Rebase с exec:

\`\`\`bash
# Выполнить команду после каждого коммита
git rebase -i --exec "npm test" main

# Если тест упадёт — rebase остановится
\`\`\`

### Изменение автора коммита:

\`\`\`bash
git rebase -i HEAD~3

# В редакторе:
edit a1b2c3d Добавить функцию

# При остановке:
git commit --amend --author="Имя <email@example.com>"
git rebase --continue
\`\`\`

## Типичные ошибки

### Ошибка: "Cannot rebase: You have unstaged changes"

\`\`\`bash
$ git rebase -i main
error: cannot rebase: You have unstaged changes.
\`\`\`

**Причина**: Незакоммиченные изменения в рабочей директории
**Решение**:
\`\`\`bash
git stash
git rebase -i main
git stash pop
\`\`\`

### Ошибка: "Could not apply..."

\`\`\`bash
$ git rebase -i main
CONFLICT (content): Could not apply a1b2c3d...
\`\`\`

**Причина**: Конфликт при применении коммита
**Решение**:
\`\`\`bash
# Разрешить конфликт
git add .
git rebase --continue
\`\`\`

### Ошибка: "Refusing to squash a merge commit"

\`\`\`bash
$ git rebase -i main
Refusing to squash a merge commit
\`\`\`

**Причина**: Попытка объединить merge commit
**Решение**: Используйте \`git rebase -p\` (preserve merges) или избегайте squash merge commits

### Ошибка: "HEAD detached"

\`\`\`bash
$ git status
HEAD detached at a1b2c3d
\`\`\`

**Причина**: Прерванный rebase или edit режим
**Решение**:
\`\`\`bash
# Продолжить rebase
git rebase --continue

# Или отменить
git rebase --abort
\`\`\`

## Когда использовать rebase?

### Подходит:
- Очистка истории перед слиянием
- Объединение мелких коммитов
- Изменение сообщений коммитов
- Удаление чувствительных данных
- Перемещение ветки на новую базу

### НЕ подходит:
- Публичные ветки (main, develop)
- Коммиты, которые уже отправлены
- Совместная работа в ветке

## Золотое правило rebase

**Никогда не делайте rebase коммитов, которые уже отправлены в публичный репозиторий!**

### Почему?

\`\`\`bash
# До rebase (на сервере):
A --- B --- C (origin/main)

# Локально сделали rebase:
A --- B --- C --- D' --- E' (локальные новые хеши)

# При push:
git push --force

# Другие разработчики имеют:
A --- B --- C --- D --- E (старые хеши)

# Результат: рассинхронизация и конфликты!
\`\`\`

### Безопасные альтернативы для публичных веток:

\`\`\`bash
# Вместо rebase — используйте revert
git revert <коммит>

# Вместо --force — используйте --force-with-lease
git push --force-with-lease
\`\`\`

## Связь с другими концепциями

- **git cherry-pick** — применяется rebase для каждого коммита
- **git reset** — часть алгоритма rebase
- **git merge** — альтернатива rebase для интеграции
- **git reflog** — восстановление после неудачного rebase
- **git filter-branch** — для массового переписывания истории
`,
      steps: [
        {
          id: 1,
          title: 'Просмотр истории',
          description: 'Посмотрите текущую историю коммитов.',
          expectedCommand: 'git log --oneline',
          hint: {
            level1: 'Посмотрите историю в компактном формате.',
            level2: 'Используйте git log с флагом --oneline.',
            level3: 'Введите: git log --oneline',
          },
        },
        {
          id: 2,
          title: 'Интерактивный rebase',
          description: 'Запустите интерактивный rebase для последних 3 коммитов.',
          expectedCommand: 'git rebase -i HEAD~3',
          hint: {
            level1: 'Запустите интерактивный rebase.',
            level2: 'Используйте git rebase -i HEAD~3.',
            level3: 'Введите: git rebase -i HEAD~3',
          },
        },
      ],
      keyInsight: {
        main: 'Интерактивный rebase — мощный инструмент для очистки истории. Используйте его до отправки изменений в публичный репозиторий.',
        example:
          'Вы сделали 5 коммитов "fix typo", "WIP", "реальная функция", "ещё исправление". Через git rebase -i HEAD~5 можно объединить их в один чистый коммит "Добавить функцию аутентификации".',
        warning:
          'НИКОГДА не делайте rebase коммитов, которые уже отправлены в публичный репозиторий! Это создаст рассинхронизацию с другими разработчиками.',
        tip: 'Используйте fixup вместо squash для автоматического объединения без редактирования сообщения. Создайте коммит с сообщением "fixup! название предыдущего коммита".',
        additional: [
          'pick — оставить коммит, reword — изменить сообщение, edit — остановиться для правок.',
          'squash — объединить с предыдущим, сохранив сообщения.',
          'drop — удалить коммит из истории.',
          'git rebase --abort отменяет текущий rebase.',
          'git rebase --continue продолжает после разрешения конфликта.',
        ],
      },
      estimatedTime: 25,
      quizQuestions: [
        {
          id: '4-1-q1',
          question:
            'Какое действие в интерактивном rebase объединяет коммит с предыдущим, сохраняя оба сообщения?',
          options: [
            {
              id: 'a',
              text: 'pick',
              isCorrect: false,
              explanation: 'Неправильно. pick оставляет коммит без изменений.',
            },
            {
              id: 'b',
              text: 'squash',
              isCorrect: true,
              explanation:
                'Правильно! squash объединяет коммиты и позволяет редактировать сообщение.',
            },
            {
              id: 'c',
              text: 'fixup',
              isCorrect: false,
              explanation: 'Неправильно. fixup объединяет, отбрасывая сообщение.',
            },
            {
              id: 'd',
              text: 'merge',
              isCorrect: false,
              explanation: 'Неправильно. merge не является действием rebase.',
            },
          ],
          explanation:
            'squash объединяет коммит с предыдущим и открывает редактор для объединения сообщений. fixup делает то же самое, но отбрасывает сообщение объединяемого коммита.',
          difficulty: 'easy',
        },
        {
          id: '4-1-q2',
          question: 'Почему нельзя делать rebase коммитов, отправленных в публичный репозиторий?',
          options: [
            {
              id: 'a',
              text: 'Rebase медленный',
              isCorrect: false,
              explanation: 'Неправильно. Скорость не при чём.',
            },
            {
              id: 'b',
              text: 'Rebase изменяет хеши коммитов, создавая рассинхронизацию',
              isCorrect: true,
              explanation: 'Правильно! Это создаст разные истории у разных разработчиков.',
            },
            {
              id: 'c',
              text: 'Rebase удаляет файлы',
              isCorrect: false,
              explanation: 'Неправильно. Rebase не удаляет файлы.',
            },
            {
              id: 'd',
              text: 'Rebase требует интернет',
              isCorrect: false,
              explanation: 'Неправильно. Rebase работает локально.',
            },
          ],
          explanation:
            'Rebase переписывает историю, создавая новые коммиты с новыми хешами. Если другие разработчики уже скачали старые коммиты, их история расходится с вашей, что приводит к конфликтам.',
          difficulty: 'medium',
        },
        {
          id: '4-1-q3',
          question: 'Какое действие rebase позволяет изменить сообщение коммита?',
          options: [
            {
              id: 'a',
              text: 'edit',
              isCorrect: false,
              explanation: 'Неправильно. edit останавливает для изменения содержимого коммита.',
            },
            {
              id: 'b',
              text: 'reword',
              isCorrect: true,
              explanation: 'Правильно! reword позволяет изменить сообщение коммита.',
            },
            {
              id: 'c',
              text: 'message',
              isCorrect: false,
              explanation: 'Неправильно. Такого действия нет.',
            },
            {
              id: 'd',
              text: 'change',
              isCorrect: false,
              explanation: 'Неправильно. Такого действия нет.',
            },
          ],
          explanation:
            'reword (или r) позволяет изменить сообщение коммита без изменения его содержимого. Git остановится и откроет редактор для ввода нового сообщения.',
          difficulty: 'easy',
        },
        {
          id: '4-1-q4',
          question: 'Как начать интерактивный rebase для последних 3 коммитов?',
          options: [
            {
              id: 'a',
              text: 'git rebase -i 3',
              isCorrect: false,
              explanation: 'Неправильно. Нужен другой синтаксис.',
            },
            {
              id: 'b',
              text: 'git rebase -i HEAD~3',
              isCorrect: true,
              explanation: 'Правильно! HEAD~3 означает "3 коммита назад от текущего".',
            },
            {
              id: 'c',
              text: 'git rebase -i --last 3',
              isCorrect: false,
              explanation: 'Неправильно. Такого флага нет.',
            },
            {
              id: 'd',
              text: 'git rebase -3',
              isCorrect: false,
              explanation: 'Неправильно. Такого синтаксиса нет.',
            },
          ],
          explanation:
            'git rebase -i HEAD~3 открывает интерактивный rebase для последних 3 коммитов. Можно также использовать хеш коммита, с которого начать.',
          difficulty: 'easy',
        },
        {
          id: '4-1-q5',
          question: 'Какое действие rebase удаляет коммит из истории?',
          options: [
            {
              id: 'a',
              text: 'remove',
              isCorrect: false,
              explanation: 'Неправильно. Такого действия нет.',
            },
            {
              id: 'b',
              text: 'delete',
              isCorrect: false,
              explanation: 'Неправильно. Такого действия нет.',
            },
            {
              id: 'c',
              text: 'drop',
              isCorrect: true,
              explanation: 'Правильно! drop удаляет коммит из истории.',
            },
            {
              id: 'd',
              text: 'skip',
              isCorrect: false,
              explanation: 'Неправильно. skip используется при конфликтах, не в todo файле.',
            },
          ],
          explanation:
            'drop (или d) полностью удаляет коммит из истории. Это полезно для удаления отладочного кода или коммитов с чувствительными данными.',
          difficulty: 'easy',
        },
        {
          id: '4-1-q6',
          question: 'Что происходит с хешами коммитов после rebase?',
          options: [
            {
              id: 'a',
              text: 'Хеши остаются прежними',
              isCorrect: false,
              explanation: 'Неправильно. Rebase создаёт новые коммиты.',
            },
            {
              id: 'b',
              text: 'Хеши изменяются',
              isCorrect: true,
              explanation: 'Правильно! Rebase создаёт новые коммиты с новыми хешами.',
            },
            {
              id: 'c',
              text: 'Хеши удаляются',
              isCorrect: false,
              explanation: 'Неправильно. Хеши не удаляются, они изменяются.',
            },
            {
              id: 'd',
              text: 'Хеши дублируются',
              isCorrect: false,
              explanation: 'Неправильно. Хеши уникальны.',
            },
          ],
          explanation:
            'Rebase применяет коммиты заново, создавая новые объекты с новыми SHA-1 хешами. Это ключевая причина, почему нельзя делать rebase публичных коммитов.',
          difficulty: 'medium',
        },
      ],
      resources: [
        {
          title: 'Git Documentation: git rebase',
          url: 'https://git-scm.com/docs/git-rebase',
          type: 'documentation',
          description: 'Официальная документация о команде rebase',
        },
        {
          title: 'Interactive Rebase Tutorial',
          url: 'https://git-scm.com/book/en/v2/Git-Tools-Rewriting-History',
          type: 'book',
          description: 'Глава Pro Git о переписывании истории',
        },
        {
          title: 'Rebase vs Merge',
          url: 'https://www.atlassian.com/git/tutorials/merging-vs-rebasing',
          type: 'article',
          description: 'Сравнение merge и rebase от Atlassian',
        },
        {
          title: 'Learn Git Branching',
          url: 'https://learngitbranching.js.org/',
          type: 'tool',
          description: 'Интерактивный визуальный туториал',
        },
      ],
    },
    {
      id: '4-2',
      moduleId: 'module-4',
      title: 'Cherry-pick',
      slug: 'cherry-pick',
      order: 2,
      theory: `# Cherry-pick

Cherry-pick позволяет применить отдельные коммиты из одной ветки в другую.

:::diagram cherry-pick:::

## История концепции cherry-pick

### Эволюция выборочного применения изменений:

| Система | Подход | Особенности |
|---------|--------|-------------|
| CVS | Ручное копирование | Медленно, ошибочно |
| Subversion | svn merge -r | Сложный синтаксис |
| **Git** | **git cherry-pick** | **Простой, точный** |

### Почему cherry-pick важен?

В распределённой разработке часто нужно:
- Перенести исправление бага в release ветку
- Выборочно применить изменения
- Переместить коммит между ветками

Название "cherry-pick" (собирать вишни) отражает идею выборочного сбора лучших плодов.

## Что такое cherry-pick?

Cherry-pick применяет изменения из указанного коммита к текущей ветке, создавая новый коммит.

\`\`\`
ДО cherry-pick:
main:      A --- B --- C
                 \\
feature:         D --- E --- F

# Хотим применить только E в main

ПОСЛЕ git cherry-pick E:
main:      A --- B --- C --- E'
                 \\
feature:         D --- E --- F
\`\`\`

Обратите внимание: E' — новый коммит с другим хешем!

## Базовое использование

### Применение одного коммита:

\`\`\`bash
# Найти хеш нужного коммита
git log --oneline feature-branch

# Применить коммит к текущей ветке
git cherry-pick <хеш-коммита>

# Пример
git cherry-pick a1b2c3d
\`\`\`

### Применение нескольких коммитов:

\`\`\`bash
# Применить несколько коммитов
git cherry-pick a1b2c3d e4f5g6h i7j8k9l

# Применить диапазон коммитов
git cherry-pick start-commit..end-commit

# Пример: применить коммиты с A по B (не включая A)
git cherry-pick a1b2c3d..e4f5g6h

# Включая начальный коммит
git cherry-pick a1b2c3d^..e4f5g6h
\`\`\`

## Глубокое погружение: Как работает cherry-pick

### Алгоритм cherry-pick:

\`\`\`
1. Определить diff коммита
   git diff <commit>^ <commit>

2. Применить diff к текущей ветке
   git apply <diff>

3. Создать новый коммит
   git commit -m "<исходное сообщение>"

4. Скопировать метаданные
   - Автор
   - Дата
   - Сообщение
\`\`\`

### Что происходит внутри:

\`\`\`bash
# Исходный коммит
$ git show a1b2c3d
commit a1b2c3d...
Author: Иван <ivan@mail.ru>
Date:   Mon Jan 15 10:00:00 2024 +0300

    Добавить функцию входа

    - Добавить форму входа
    - Добавить валидацию

# После cherry-pick
$ git log -1
commit e4f5g6h...  # Новый хеш!
Author: Иван <ivan@mail.ru>  # Автор сохранён
Date:   Mon Jan 15 10:00:00 2024 +0300  # Дата сохранена

    Добавить функцию входа

    - Добавить форму входа
    - Добавить валидацию
\`\`\`

### Структура cherry-pick коммита:

\`\`\`bash
# Обычный коммит
tree: t1e2s3t...
parent: p1a2r3e...  # Один родитель

# Cherry-pick коммит — тоже обычный!
tree: t1e2s3t...
parent: c1u2r3r...  # Родитель — текущая ветка

# Но можно добавить информацию об источнике
git cherry-pick -x a1b2c3d

# В сообщении появится:
(cherry picked from commit a1b2c3d...)
\`\`\`

## Опции cherry-pick

### -x — добавить информацию об источнике:

\`\`\`bash
git cherry-pick -x a1b2c3d

# Сообщение будет:
Добавить функцию входа

(cherry picked from commit a1b2c3d...)
\`\`\`

Полезно для отслеживания происхождения изменений.

### -n / --no-commit — применить без коммита:

\`\`\`bash
# Применить изменения в индекс
git cherry-pick -n a1b2c3d

# Изменения в индексе, можно редактировать
git status
# Changes to be committed:
#   modified: src/auth.ts

# Затем закоммитить вручную
git commit -m "Применить изменения из feature"
\`\`\`

### -e / --edit — редактировать сообщение:

\`\`\`bash
git cherry-pick -e a1b2c3d
# Откроется редактор для изменения сообщения
\`\`\`

### -s / --signoff — добавить Signed-off-by:

\`\`\`bash
git cherry-pick -s a1b2c3d

# В сообщении появится:
Signed-off-by: Ваше Имя <email@example.com>
\`\`\`

### --ff — fast-forward если возможно:

\`\`\`bash
# Если cherry-pick применяется к тому же коммиту
git cherry-pick --ff a1b2c3d
\`\`\`

## Разрешение конфликтов

### При возникновении конфликта:

\`\`\`bash
$ git cherry-pick a1b2c3d
error: could not apply a1b2c3d... Добавить функцию
hint: after resolving the conflicts, mark the corrected paths
hint: with 'git add <paths>' or 'git rm <paths>'
\`\`\`

### Шаги разрешения:

\`\`\`bash
# 1. Посмотреть конфликтующие файлы
git status

# 2. Разрешить конфликты
vim src/auth.ts

# 3. Добавить разрешённые файлы
git add src/auth.ts

# 4. Продолжить cherry-pick
git cherry-pick --continue

# Или отменить
git cherry-pick --abort
\`\`\`

### Принятие одной из версий:

\`\`\`bash
# Принять нашу версию (текущая ветка)
git checkout --ours src/auth.ts

# Принять их версию (cherry-pick коммит)
git checkout --theirs src/auth.ts

# Добавить и продолжить
git add src/auth.ts
git cherry-pick --continue
\`\`\`

## Практические кейсы

### Кейс 1: "Hotfix в release ветку"

\`\`\`bash
# Баг исправлен в develop
git log --oneline develop
# a1b2c3d Исправить критический баг

# Перенести в release ветку
git checkout release/v1.0
git cherry-pick -x a1b2c3d

# Результат:
# e4f5g6h Исправить критический баг
# (cherry picked from commit a1b2c3d...)
\`\`\`

### Кейс 2: "Выборочное применение из PR"

\`\`\`bash
# PR содержит 5 коммитов, нужны только 2
git log --oneline feature/pr-123
# m9n8o7p Добавить тесты
# l6k5j4i Рефакторинг
# k3h2g1f Добавить функцию  # Нужен
# j0e9d8c Исправить опечатку
# b7a6s5d Добавить документацию  # Нужен

# Применить только нужные
git checkout main
git cherry-pick b7a6s5d k3h2g1f
\`\`\`

### Кейс 3: "Отмена неудачного слияния"

\`\`\`bash
# Случайно слили ветку
git merge feature

# Откатить
git reset --hard HEAD~1

# Применить нужные коммиты по отдельности
git cherry-pick a1b2c3d e4f5g6h
\`\`\`

### Кейс 4: "Перенос коммита в другую ветку"

\`\`\`bash
# Коммит сделан в неправильной ветке
git checkout feature-wrong
git log --oneline -1
# a1b2c3d Добавить функцию X

# Перенести в правильную ветку
git checkout feature-right
git cherry-pick a1b2c3d

# Удалить из неправильной
git checkout feature-wrong
git rebase -i HEAD~1  # drop a1b2c3d
\`\`\`

### Кейс 5: "Объединение cherry-pick в один коммит"

\`\`\`bash
# Несколько cherry-pick в один коммит
git cherry-pick -n a1b2c3d
git cherry-pick -n e4f5g6h
git cherry-pick -n i7j8k9l

# Все изменения в индексе
git status

# Один коммит вместо трёх
git commit -m "Применить изменения из feature ветки"
\`\`\`

## Типичные ошибки

### Ошибка: "The previous cherry-pick is now empty"

\`\`\`bash
$ git cherry-pick a1b2c3d
The previous cherry-pick is now empty, possibly due to conflict resolution.
\`\`\`

**Причина**: Изменения уже применены в текущей ветке
**Решение**:
\`\`\`bash
# Пропустить пустой коммит
git cherry-pick --skip

# Или отменить
git cherry-pick --abort
\`\`\`

### Ошибка: "could not apply..."

\`\`\`bash
$ git cherry-pick a1b2c3d
error: could not apply a1b2c3d... Добавить функцию
\`\`\`

**Причина**: Конфликт при применении изменений
**Решение**: Разрешить конфликт и продолжить

### Ошибка: "bad revision"

\`\`\`bash
$ git cherry-pick xyz
fatal: bad revision 'xyz'
\`\`\`

**Причина**: Неверный хеш коммита
**Решение**: Проверьте хеш через \`git log\`

## Продвинутые техники

### Cherry-pick с диапазоном:

\`\`\`bash
# Применить все коммиты с A по B
git cherry-pick A..B

# Включая A
git cherry-pick A^..B

# С exclude
git cherry-pick A..B --not C
\`\`\`

### Cherry-pick с rebasing:

\`\`\`bash
# Вместо cherry-pick диапазона
git rebase --onto main feature~3 feature

# Эквивалентно cherry-pick последних 3 коммитов feature на main
\`\`\`

### Поиск коммитов для cherry-pick:

\`\`\`bash
# Найти коммиты в feature, которых нет в main
git log main..feature --oneline

# Найти коммиты с определённым сообщением
git log --grep="bugfix" --oneline

# Найти коммиты автора
git log --author="Иван" --oneline
\`\`\`

## Когда использовать cherry-pick?

### Подходит:
- Исправление бага, которое нужно перенести в release ветку
- Выборочное применение изменений
- Перенос конкретных функций
- Восстановление потерянных коммитов

### НЕ подходит:
- Массовое применение изменений (лучше merge)
- Регулярная синхронизация веток (лучше rebase)
- Слияние feature веток (лучше merge)

## Связь с другими концепциями

- **git rebase** — использует cherry-pick для применения коммитов
- **git merge** — альтернатива для объединения всех изменений
- **git format-patch** — создание патчей для переноса
- **git revert** — обратная операция (отмена коммита)
- **git reflog** — восстановление после неудачного cherry-pick
`,
      steps: [
        {
          id: 1,
          title: 'Просмотр коммитов',
          description: 'Посмотрите историю, чтобы найти хеш коммита для cherry-pick.',
          expectedCommand: 'git log --oneline',
          hint: {
            level1: 'Посмотрите историю коммитов.',
            level2: 'Используйте git log --oneline.',
            level3: 'Введите: git log --oneline',
          },
        },
        {
          id: 2,
          title: 'Cherry-pick коммита',
          description: 'Примените последний коммит из ветки feature с помощью cherry-pick.',
          expectedCommand: 'git cherry-pick feature',
          hint: {
            level1: 'Примените изменения из другой ветки.',
            level2: 'Используйте git cherry-pick с именем ветки или хешем.',
            level3: 'Введите: git cherry-pick feature',
          },
        },
      ],
      keyInsight: {
        main: 'Cherry-pick полезен для выборочного переноса изменений между ветками.',
        example:
          'Вы исправили критический баг в ветке develop, но нужно перенести только это исправление в release ветку. Найдите хеш коммита с исправлением и выполните git cherry-pick <хеш> в release ветке.',
        warning:
          'Cherry-pick создаёт новый коммит с другим хешем! Это может запутать историю при последующих слияниях. Используйте только когда merge или rebase не подходят.',
        tip: 'Используйте git cherry-pick -x для добавления информации об исходном коммите в сообщение. Это поможет отследить происхождение изменения.',
        additional: [
          'git cherry-pick --continue после разрешения конфликта.',
          'git cherry-pick --abort отменяет операцию.',
          'git cherry-pick -n применяет изменения без создания коммита.',
          'git cherry-pick -x добавляет информацию об источнике.',
        ],
      },
      estimatedTime: 20,
      quizQuestions: [
        {
          id: '4-2-q1',
          question: 'Что делает git cherry-pick?',
          options: [
            {
              id: 'a',
              text: 'Копирует всю ветку',
              isCorrect: false,
              explanation: 'Неправильно. Cherry-pick копирует отдельные коммиты.',
            },
            {
              id: 'b',
              text: 'Применяет указанный коммит к текущей ветке',
              isCorrect: true,
              explanation: 'Правильно! Cherry-pick создаёт новый коммит с теми же изменениями.',
            },
            {
              id: 'c',
              text: 'Удаляет коммит из ветки',
              isCorrect: false,
              explanation: 'Неправильно. Для удаления используйте rebase или revert.',
            },
            {
              id: 'd',
              text: 'Сравнивает две ветки',
              isCorrect: false,
              explanation: 'Неправильно. Для сравнения используйте diff.',
            },
          ],
          explanation:
            'git cherry-pick применяет изменения из указанного коммита к текущей ветке, создавая новый коммит с другим хешем.',
          difficulty: 'easy',
        },
        {
          id: '4-2-q2',
          question: 'Какой флаг cherry-pick добавляет информацию об исходном коммите?',
          options: [
            {
              id: 'a',
              text: '-s',
              isCorrect: false,
              explanation: 'Неправильно. -s используется для подписи (signoff).',
            },
            {
              id: 'b',
              text: '-x',
              isCorrect: true,
              explanation:
                'Правильно! -x добавляет строку "(cherry picked from commit...)" в сообщение.',
            },
            {
              id: 'c',
              text: '-i',
              isCorrect: false,
              explanation: 'Неправильно. -i не используется в cherry-pick.',
            },
            {
              id: 'd',
              text: '-o',
              isCorrect: false,
              explanation: 'Неправильно. Такого флага нет.',
            },
          ],
          explanation:
            'git cherry-pick -x добавляет в сообщение коммита строку "(cherry picked from commit <хеш>)". Это помогает отследить происхождение изменения.',
          difficulty: 'medium',
        },
        {
          id: '4-2-q3',
          question: 'Как применить изменения коммита без создания нового коммита?',
          options: [
            {
              id: 'a',
              text: 'git cherry-pick --no-commit',
              isCorrect: true,
              explanation: 'Правильно! --no-commit применяет изменения в индекс.',
            },
            {
              id: 'b',
              text: 'git cherry-pick --soft',
              isCorrect: false,
              explanation: 'Неправильно. Такого флага нет.',
            },
            {
              id: 'c',
              text: 'git cherry-pick --apply',
              isCorrect: false,
              explanation: 'Неправильно. Такого флага нет.',
            },
            {
              id: 'd',
              text: 'git cherry-pick --draft',
              isCorrect: false,
              explanation: 'Неправильно. Такого флага нет.',
            },
          ],
          explanation:
            'git cherry-pick --no-commit (или -n) применяет изменения в индекс без создания коммита. Это позволяет объединить несколько cherry-pick в один коммит.',
          difficulty: 'medium',
        },
        {
          id: '4-2-q4',
          question: 'Когда следует использовать cherry-pick?',
          options: [
            {
              id: 'a',
              text: 'Для слияния всех изменений из ветки',
              isCorrect: false,
              explanation: 'Неправильно. Для этого используйте merge.',
            },
            {
              id: 'b',
              text: 'Для переноса конкретного исправления в release ветку',
              isCorrect: true,
              explanation: 'Правильно! Это типичный сценарий использования cherry-pick.',
            },
            {
              id: 'c',
              text: 'Для удаления коммита',
              isCorrect: false,
              explanation: 'Неправильно. Для этого используйте rebase или revert.',
            },
            {
              id: 'd',
              text: 'Для создания новой ветки',
              isCorrect: false,
              explanation: 'Неправильно. Для этого используйте branch.',
            },
          ],
          explanation:
            'Cherry-pick идеален для переноса отдельных исправлений (hotfix) в release ветки, когда полное слияние не требуется или невозможно.',
          difficulty: 'easy',
        },
        {
          id: '4-2-q5',
          question: 'Что происходит с хешем коммита после cherry-pick?',
          options: [
            {
              id: 'a',
              text: 'Хеш остаётся прежним',
              isCorrect: false,
              explanation: 'Неправильно. Cherry-pick создаёт новый коммит.',
            },
            {
              id: 'b',
              text: 'Хеш изменяется',
              isCorrect: true,
              explanation: 'Правильно! Новый коммит имеет новый хеш.',
            },
            {
              id: 'c',
              text: 'Хеш удаляется',
              isCorrect: false,
              explanation: 'Неправильно. Хеш не удаляется.',
            },
            {
              id: 'd',
              text: 'Хеш дублируется',
              isCorrect: false,
              explanation: 'Неправильно. Хеши уникальны.',
            },
          ],
          explanation:
            'Cherry-pick создаёт новый коммит с новым хешем. Исходный коммит остаётся в своей ветке без изменений.',
          difficulty: 'easy',
        },
      ],
      resources: [
        {
          title: 'Git Documentation: git cherry-pick',
          url: 'https://git-scm.com/docs/git-cherry-pick',
          type: 'documentation',
          description: 'Официальная документация о команде cherry-pick',
        },
        {
          title: 'Cherry-picking Explained',
          url: 'https://www.atlassian.com/git/tutorials/cherry-pick',
          type: 'article',
          description: 'Туториал по cherry-pick от Atlassian',
        },
        {
          title: 'When to Use Cherry-pick',
          url: 'https://dev.to/neshaz/how-to-use-git-cherry-pick-4hag',
          type: 'article',
          description: 'Статья о сценариях использования cherry-pick',
        },
      ],
    },
    {
      id: '4-3',
      moduleId: 'module-4',
      title: 'Reset vs Revert',
      slug: 'reset-vs-revert',
      order: 3,
      theory: `# Reset vs Revert

Два способа отменить изменения в Git с разными последствиями.

:::diagram reset-revert:::

## История концепции отката изменений

### Эволюция подходов:

| Система | Откат изменений | Проблема |
|---------|-----------------|----------|
| CVS | Нет отката | Только новые коммиты |
| Subversion | svn merge --reverse | Сложно, централизованно |
| **Git** | **reset / revert** | **Гибко, локально** |

### Почему два способа?

Git предоставляет два механизма отката:

1. **Reset** — переписывает историю (удаляет коммиты)
2. **Revert** — создаёт новые коммиты (сохраняет историю)

Выбор зависит от контекста: локальные или публичные изменения.

## Git Reset

Reset перемещает указатель ветки назад, удаляя историю.

### Три режима reset:

\`\`\`bash
# Мягкий reset — изменения в индексе
git reset --soft HEAD~1

# Смешанный reset (по умолчанию) — изменения в рабочей директории
git reset --mixed HEAD~1
git reset HEAD~1  # --mixed по умолчанию

# Жёсткий reset — изменения потеряны
git reset --hard HEAD~1
\`\`\`

### Уровни reset:

| Режим | HEAD | Индекс | Рабочая директория | Использование |
|-------|------|--------|-------------------|---------------|
| --soft | ✓ | | | Пересоздание коммита |
| --mixed | ✓ | ✓ | | Пересмотр индекса |
| --hard | ✓ | ✓ | ✓ | Полный откат |

### Визуализация:

\`\`\`
ДО reset:
HEAD → C (последний коммит)
Индекс: состояние C
Рабочая директория: состояние C

ПОСЛЕ git reset --soft HEAD~1:
HEAD → B (предыдущий коммит)
Индекс: состояние C (сохранено!)
Рабочая директория: состояние C (сохранено!)

ПОСЛЕ git reset --mixed HEAD~1:
HEAD → B
Индекс: состояние B (сброшено)
Рабочая директория: состояние C (сохранено!)

ПОСЛЕ git reset --hard HEAD~1:
HEAD → B
Индекс: состояние B
Рабочая директория: состояние B (все изменения потеряны!)
\`\`\`

## Глубокое погружение: Как работает reset

### Механизм reset:

\`\`\`bash
# reset --soft
1. Переместить HEAD на указанный коммит
   .git/refs/heads/main обновляется

# reset --mixed (по умолчанию)
1. Переместить HEAD
2. Сбросить индекс (git reset HEAD)
   .git/index обновляется

# reset --hard
1. Переместить HEAD
2. Сбросить индекс
3. Обновить рабочую директорию
   Файлы на диске заменяются
\`\`\`

### Внутренняя структура:

\`\`\`bash
# До reset
.git/
├── HEAD → refs/heads/main
├── refs/
│   └── heads/
│       └── main → a1b2c3d (коммит C)
└── index → состояние C

# После git reset --soft HEAD~1
.git/
├── HEAD → refs/heads/main
├── refs/
│   └── heads/
│       └── main → e4f5g6h (коммит B)  # Изменено!
└── index → состояние C  # Не изменено

# После git reset --hard HEAD~1
.git/
├── HEAD → refs/heads/main
├── refs/
│   └── heads/
│       └── main → e4f5g6h (коммит B)
└── index → состояние B  # Сброшено
\`\`\`

## Git Revert

Revert создаёт новый коммит, который отменяет указанный коммит.

### Базовое использование:

\`\`\`bash
# Отменить последний коммит
git revert HEAD

# Отменить конкретный коммит
git revert a1b2c3d

# Отменить несколько коммитов
git revert a1b2c3d e4f5g6h

# Отменить диапазон (не включая start)
git revert start..end
\`\`\`

### Как работает revert:

\`\`\`
ДО revert:
main: A --- B --- C

# git revert C

ПОСЛЕ revert:
main: A --- B --- C --- C'
                    (отменяет C)
\`\`\`

### Структура revert коммита:

\`\`\`bash
$ git revert a1b2c3d

# Откроется редактор:
Revert "Добавить функцию входа"

This reverts commit a1b2c3d.

# Signed-off-by: Ваше Имя <email@example.com>
\`\`\`

## Сравнение Reset vs Revert

### Ключевые различия:

| Аспект | Reset | Revert |
|--------|-------|--------|
| История | Переписывает | Сохраняет |
| Новые коммиты | Нет | Да |
| Безопасность | Опасно для публичных | Безопасно |
| Откат нескольких коммитов | Да | Да |
| Конфликты | Нет | Возможны |

### Когда что использовать?

\`\`\`bash
# RESET — для локальных изменений
- Коммит не отправлен на сервер
- Нужно переписать историю
- Хотите объединить коммиты

# REVERT — для публичных изменений
- Коммит уже отправлен
- История не должна меняться
- Другие разработчики используют ветку
\`\`\`

## Практические кейсы

### Кейс 1: "Отмена локального коммита"

\`\`\`bash
# Сделали коммит с ошибкой
git commit -m "Добавить функцию"
# Забыли добавить файл!

# Вариант 1: amend (если ещё не push)
git add forgotten-file.txt
git commit --amend --no-edit

# Вариант 2: reset --soft
git reset --soft HEAD~1
git add forgotten-file.txt
git commit -m "Добавить функцию (исправлено)"
\`\`\`

### Кейс 2: "Отмена публичного коммита"

\`\`\`bash
# Коммит уже отправлен на сервер
git push origin main

# Обнаружили ошибку
# НЕЛЬЗЯ использовать reset!

# Используем revert
git revert HEAD
git push origin main

# История:
# A --- B --- C --- C'
#             ^     ^
#           ошибка  отмена
\`\`\`

### Кейс 3: "Отмена merge коммита"

\`\`\`bash
# Слияние ветки
git merge feature
# Создан merge commit M

# Отмена merge
git revert -m 1 HEAD

# -m 1 означает: оставить изменения первого родителя (main)
# -m 2 означало бы: оставить изменения feature
\`\`\`

### Кейс 4: "Полный откат к определённому состоянию"

\`\`\`bash
# Вернуться к коммиту B, потеряв все изменения
git reset --hard B

# Восстановление (если передумали)
git reflog
# a1b2c3d HEAD@{0}: reset: moving to B
# e4f5g6h HEAD@{1}: commit: C

git reset --hard e4f5g6h
\`\`\`

### Кейс 5: "Выборочный откат файлов"

\`\`\`bash
# Откатить конкретный файл к предыдущему состоянию
git checkout HEAD~1 -- src/file.ts

# Или с restore (Git 2.23+)
git restore --source=HEAD~1 src/file.ts

# Это не reset и не revert, но полезно знать
\`\`\`

## Продвинутые техники

### Reset с патчем:

\`\`\`bash
# Интерактивный reset (выбор файлов)
git reset -p HEAD~1
\`\`\`

### Revert без коммита:

\`\`\`bash
# Применить revert без создания коммита
git revert -n HEAD

# Можно добавить другие изменения
git add other-file.ts

# Затем закоммитить
git commit -m "Отменить последнее изменение и исправить другое"
\`\`\`

### Отмена revert:

\`\`\`bash
# Revert можно отменить... другим revert!
git revert HEAD  # Отменил коммит C
git revert HEAD  # Отменит revert, вернёт C
\`\`\`

### Восстановление после reset --hard:

\`\`\`bash
# Случайно сделали reset --hard
git reset --hard HEAD~5
# Потеряли 5 коммитов!

# Восстановление через reflog
git reflog
# a1b2c3d HEAD@{0}: reset: moving to HEAD~5
# e4f5g6h HEAD@{1}: commit: Последний потерянный коммит

# Вернуться
git reset --hard e4f5g6h
\`\`\`

## Типичные ошибки

### Ошибка: "Cannot reset with --hard when there are uncommitted changes"

\`\`\`bash
$ git reset --hard HEAD~1
error: Cannot reset with --hard when there are uncommitted changes.
\`\`\`

**Причина**: Незакоммиченные изменения будут потеряны
**Решение**:
\`\`\`bash
# Сохранить изменения
git stash

# Выполнить reset
git reset --hard HEAD~1

# Вернуть изменения
git stash pop
\`\`\`

### Ошибка: "Revert conflict"

\`\`\`bash
$ git revert a1b2c3d
error: could not revert a1b2c3d... 
CONFLICT (content): Merge conflict in src/file.ts
\`\`\`

**Причина**: Конфликт при применении обратных изменений
**Решение**:
\`\`\`bash
# Разрешить конфликт
vim src/file.ts
git add src/file.ts
git revert --continue
\`\`\`

### Ошибка: "is a merge but no -m option was given"

\`\`\`bash
$ git revert HEAD
error: commit HEAD is a merge but no -m option was given.
\`\`\`

**Причина**: Попытка revert merge коммита без указания родителя
**Решение**:
\`\`\`bash
# Указать, какого родителя оставить
git revert -m 1 HEAD  # Оставить первого родителя (main)
\`\`\`

## Правила безопасности

### Золотые правила:

1. **Никогда не reset публичные ветки!**
   \`\`\`bash
   # ПЛОХО
   git reset --hard HEAD~1
   git push --force origin main  # Опасно!

   # ХОРОШО
   git revert HEAD
   git push origin main  # Безопасно
   \`\`\`

2. **Всегда проверяйте git status перед reset --hard**
   \`\`\`bash
   git status
   git stash  # Если есть изменения
   git reset --hard HEAD~1
   \`\`\`

3. **Используйте reflog для восстановления**
   \`\`\`bash
   git reflog  # Всегда можно восстановить
   \`\`\`

4. **Предпочитайте --force-with-lease вместо --force**
   \`\`\`bash
   # Безопаснее
   git push --force-with-lease origin main
   \`\`\`

## Связь с другими концепциями

- **git commit --amend** — альтернатива reset --soft для последнего коммита
- **git rebase** — использует reset для переписывания истории
- **git reflog** — восстановление после reset
- **git stash** — сохранение изменений перед reset
- **git checkout** — откат файлов (не коммитов)
`,
      steps: [
        {
          id: 1,
          title: 'Просмотр истории',
          description: 'Посмотрите историю коммитов.',
          expectedCommand: 'git log --oneline',
          hint: {
            level1: 'Посмотрите историю.',
            level2: 'Используйте git log --oneline.',
            level3: 'Введите: git log --oneline',
          },
        },
        {
          id: 2,
          title: 'Мягкий reset',
          description: 'Выполните мягкий reset на один коммит назад.',
          expectedCommand: 'git reset --soft HEAD~1',
          hint: {
            level1: 'Отмените последний коммит, сохранив изменения.',
            level2: 'Используйте git reset --soft HEAD~1.',
            level3: 'Введите: git reset --soft HEAD~1',
          },
        },
        {
          id: 3,
          title: 'Проверка статуса',
          description: 'Проверьте состояние после reset.',
          expectedCommand: 'git status',
          hint: {
            level1: 'Проверьте, что произошло с изменениями.',
            level2: 'Используйте git status.',
            level3: 'Введите: git status',
          },
        },
      ],
      keyInsight: {
        main: 'Reset переписывает историю, revert создаёт новый коммит. Выбирайте в зависимости от контекста.',
        example:
          'Локальный коммит с ошибкой: git reset --soft HEAD~1 отменит его, сохранив изменения в индексе. Публичный коммит с ошибкой: git revert HEAD создаст новый коммит, отменяющий изменения.',
        warning:
          'git reset --hard удаляет изменения безвозвратно! Всегда проверяйте git status и git stash перед жёстким reset.',
        tip: 'Используйте git reset --soft для пересоздания коммита с другими изменениями. --mixed (по умолчанию) — для пересмотра того, что попадёт в коммит.',
        additional: [
          '--soft перемещает HEAD, сохраняя индекс и рабочую директорию.',
          '--mixed перемещает HEAD и сбрасывает индекс.',
          '--hard перемещает HEAD, сбрасывает индекс и рабочую директорию.',
          'git reflog помогает восстановиться после неудачного reset.',
          'git revert -m 1 HEAD отменяет merge коммит.',
        ],
      },
      estimatedTime: 25,
      quizQuestions: [
        {
          id: '4-3-q1',
          question: 'Какой режим reset сохраняет изменения в индексе?',
          options: [
            {
              id: 'a',
              text: 'git reset --hard',
              isCorrect: false,
              explanation: 'Неправильно. --hard удаляет все изменения.',
            },
            {
              id: 'b',
              text: 'git reset --soft',
              isCorrect: true,
              explanation: 'Правильно! --soft перемещает HEAD, сохраняя изменения в индексе.',
            },
            {
              id: 'c',
              text: 'git reset --mixed',
              isCorrect: false,
              explanation: 'Неправильно. --mixed сбрасывает индекс.',
            },
            {
              id: 'd',
              text: 'git reset --keep',
              isCorrect: false,
              explanation: 'Неправильно. --keep пытается сохранить рабочую директорию.',
            },
          ],
          explanation:
            'git reset --soft перемещает указатель HEAD, но сохраняет изменения в индексе (staging area) и рабочей директории. Это полезно для пересоздания коммита.',
          difficulty: 'easy',
        },
        {
          id: '4-3-q2',
          question: 'В чём разница между reset и revert?',
          options: [
            {
              id: 'a',
              text: 'Нет разницы',
              isCorrect: false,
              explanation: 'Неправильно. Это разные команды.',
            },
            {
              id: 'b',
              text: 'Reset переписывает историю, revert создаёт новый коммит',
              isCorrect: true,
              explanation: 'Правильно! Reset опасен для публичных веток, revert безопасен.',
            },
            {
              id: 'c',
              text: 'Reset для файлов, revert для коммитов',
              isCorrect: false,
              explanation: 'Неправильно. Обе команды работают с коммитами.',
            },
            {
              id: 'd',
              text: 'Reset быстрее, revert медленнее',
              isCorrect: false,
              explanation: 'Неправильно. Скорость не главное отличие.',
            },
          ],
          explanation:
            'git reset перемещает указатель ветки назад, удаляя коммиты из истории. git revert создаёт новый коммит, который отменяет изменения указанного коммита, сохраняя историю.',
          difficulty: 'medium',
        },
        {
          id: '4-3-q3',
          question: 'Какую команду безопасно использовать для отмены публичного коммита?',
          options: [
            {
              id: 'a',
              text: 'git reset --hard HEAD~1',
              isCorrect: false,
              explanation: 'Неправильно. Это перепишет историю и создаст проблемы для других.',
            },
            {
              id: 'b',
              text: 'git revert HEAD',
              isCorrect: true,
              explanation: 'Правильно! revert создаёт новый коммит, не переписывая историю.',
            },
            {
              id: 'c',
              text: 'git reset --soft HEAD~1',
              isCorrect: false,
              explanation: 'Неправильно. Любой reset переписывает историю.',
            },
            {
              id: 'd',
              text: 'git delete HEAD',
              isCorrect: false,
              explanation: 'Неправильно. Такой команды нет.',
            },
          ],
          explanation:
            'git revert безопасен для публичных веток, потому что создаёт новый коммит, отменяющий изменения, вместо удаления коммита из истории. Это не создаёт проблем для других разработчиков.',
          difficulty: 'medium',
        },
        {
          id: '4-3-q4',
          question: 'Что делает git reset --hard HEAD~1?',
          options: [
            {
              id: 'a',
              text: 'Отменяет последний коммит, сохраняя изменения',
              isCorrect: false,
              explanation: 'Неправильно. --hard удаляет изменения.',
            },
            {
              id: 'b',
              text: 'Удаляет последний коммит и все незафиксированные изменения',
              isCorrect: true,
              explanation: 'Правильно! --hard — самый агрессивный режим reset.',
            },
            {
              id: 'c',
              text: 'Создаёт резервную копию последнего коммита',
              isCorrect: false,
              explanation: 'Неправильно. reset не создаёт резервных копий.',
            },
            {
              id: 'd',
              text: 'Перемещает HEAD на первый коммит',
              isCorrect: false,
              explanation: 'Неправильно. HEAD~1 означает "один коммит назад".',
            },
          ],
          explanation:
            'git reset --hard HEAD~1 перемещает HEAD на один коммит назад, сбрасывает индекс и удаляет все изменения в рабочей директории. Это необратимая операция!',
          difficulty: 'easy',
        },
        {
          id: '4-3-q5',
          question: 'Как отменить merge коммит?',
          options: [
            {
              id: 'a',
              text: 'git revert HEAD',
              isCorrect: false,
              explanation: 'Неправильно. Для merge коммита нужен флаг -m.',
            },
            {
              id: 'b',
              text: 'git revert -m 1 HEAD',
              isCorrect: true,
              explanation: 'Правильно! -m 1 указывает оставить первого родителя.',
            },
            {
              id: 'c',
              text: 'git reset HEAD',
              isCorrect: false,
              explanation: 'Неправильно. reset не подходит для публичных merge коммитов.',
            },
            {
              id: 'd',
              text: 'git unmerge HEAD',
              isCorrect: false,
              explanation: 'Неправильно. Такой команды нет.',
            },
          ],
          explanation:
            'git revert -m 1 HEAD отменяет merge коммит, оставляя изменения первого родителя (обычно main). -m 2 оставил бы изменения сливаемой ветки.',
          difficulty: 'hard',
        },
      ],
      resources: [
        {
          title: 'Git Documentation: git reset',
          url: 'https://git-scm.com/docs/git-reset',
          type: 'documentation',
          description: 'Официальная документация о команде reset',
        },
        {
          title: 'Git Documentation: git revert',
          url: 'https://git-scm.com/docs/git-revert',
          type: 'documentation',
          description: 'Официальная документация о команде revert',
        },
        {
          title: 'Reset vs Revert vs Checkout',
          url: 'https://www.atlassian.com/git/tutorials/resetting-checking-out-and-reverting',
          type: 'article',
          description: 'Сравнение команд отката изменений от Atlassian',
        },
      ],
    },
    {
      id: '4-4',
      moduleId: 'module-4',
      title: 'Изменение коммитов',
      slug: 'amending-commits',
      order: 4,
      theory: `# Изменение коммитов

Иногда нужно изменить последний коммит после его создания.

:::diagram amend-flow:::

## История концепции amend

### Эволюция подходов к исправлению коммитов:

| Система | Подход | Особенности |
|---------|--------|-------------|
| CVS | Нет возможности | Только новые коммиты |
| Subversion | Нет возможности | Только новые коммиты |
| **Git** | **--amend** | **Изменение последнего коммита** |

### Почему amend важен?

В распределённых системах коммит сначала создаётся локально. Это даёт возможность исправить его до отправки:

- Забыли добавить файл
- Опечатались в сообщении
- Нужно добавить небольшие изменения
- Хотите изменить автора

## Что такое amend?

Amend заменяет последний коммит новым, который включает дополнительные изменения.

\`\`\`
ДО amend:
A --- B --- C (последний коммит)

# git commit --amend

ПОСЛЕ amend:
A --- B --- C' (новый коммит с изменениями)
\`\`\`

**Важно**: C' имеет другой хеш, хотя содержит изменения C + новые!

## Основные сценарии использования

### 1. Изменение сообщения коммита

\`\`\`bash
# Изменить сообщение последнего коммита
git commit --amend -m "Новое сообщение коммита"

# Или открыть редактор
git commit --amend
\`\`\`

### 2. Добавление забытых файлов

\`\`\`bash
# Забыли добавить файл
git add forgotten-file.txt

# Добавить в последний коммит без изменения сообщения
git commit --amend --no-edit
\`\`\`

### 3. Изменение содержимого

\`\`\`bash
# Внести изменения
vim src/file.ts

# Добавить в индекс
git add src/file.ts

# Изменить коммит
git commit --amend --no-edit
\`\`\`

### 4. Изменение автора

\`\`\`bash
# Изменить автора последнего коммита
git commit --amend --author="Имя <email@example.com>"

# Или использовать текущего пользователя
git commit --amend --reset-author --no-edit
\`\`\`

## Глубокое погружение: Как работает amend

### Механизм amend:

\`\`\`bash
# amend выполняет:
1. Берёт изменения из индекса
2. Берёт дерево последнего коммита
3. Объединяет изменения
4. Создаёт новый коммит
5. Перемещает указатель ветки на новый коммит

# Технически:
git cat-file -p HEAD  # Получить последний коммит
git write-tree        # Создать новое дерево
git commit-tree       # Создать новый коммит
git update-ref        # Обновить указатель ветки
\`\`\`

### Что происходит с хешем:

\`\`\`bash
# Исходный коммит
$ git log -1
commit a1b2c3d4e5f6g7h8i9j0...  # Хеш A
Author: Иван <ivan@mail.ru>
Date:   Mon Jan 15 10:00:00 2024 +0300

    Добавить функцию

# После amend
$ git commit --amend -m "Добавить функцию входа"

$ git log -1
commit m9n8o7p6q5r4s3t2u1v0...  # Хеш B (другой!)
Author: Иван <ivan@mail.ru>
Date:   Mon Jan 15 10:00:00 2024 +0300

    Добавить функцию входа
\`\`\`

### Структура коммита до и после:

\`\`\`bash
# До amend
commit a1b2c3d
tree: t1e2s3t
parent: p1a2r3e
author: Иван <ivan@mail.ru> 1234567890 +0300
committer: Иван <ivan@mail.ru> 1234567890 +0300

Добавить функцию

# После amend (изменили сообщение)
commit m9n8o7p  # Новый хеш!
tree: t1e2s3t   # То же дерево (если не добавляли файлы)
parent: p1a2r3e # Тот же родитель
author: Иван <ivan@mail.ru> 1234567890 +0300
committer: Иван <ivan@mail.ru> 1234567900 +0300  # Новое время!

Добавить функцию входа  # Новое сообщение
\`\`\`

## Практические кейсы

### Кейс 1: "Забыл добавить файл"

\`\`\`bash
# Сделали коммит
git commit -m "Добавить функцию аутентификации"

# Вспомнили, что забыли тест
git add tests/auth.test.ts
git commit --amend --no-edit

# Теперь тест включён в коммит
\`\`\`

### Кейс 2: "Опечатка в сообщении"

\`\`\`bash
# Коммит с опечаткой
git commit -m "Добавить фунцию входа"  # Опечатка!

# Исправить сообщение
git commit --amend -m "Добавить функцию входа"
\`\`\`

### Кейс 3: "Приведение к Conventional Commits"

\`\`\`bash
# Коммит с плохим сообщением
git commit -m "fix"

# Привести к стандарту
git commit --amend -m "fix(auth): исправить валидацию пароля"
\`\`\`

### Кейс 4: "Изменение автора"

\`\`\`bash
# Коммит сделан с неправильным email
git commit -m "Добавить функцию"
# Автор: wrong@email.com

# Исправить автора
git commit --amend --author="Правильное Имя <correct@email.com>"

# Или использовать глобальные настройки
git config user.name "Правильное Имя"
git config user.email "correct@email.com"
git commit --amend --reset-author --no-edit
\`\`\`

### Кейс 5: "Разделение изменений"

\`\`\`bash
# Случайно включили изменения двух функций в один коммит
git add .
git commit -m "Добавить функции A и B"

# Хотим разделить на два коммита
git reset HEAD~1

# Создать два коммита
git add feature-a/
git commit -m "Добавить функцию A"
git add feature-b/
git commit -m "Добавить функцию B"
\`\`\`

## Продвинутые техники

### Amend с интерактивным выбором:

\`\`\`bash
# Выбрать, что добавить в amend
git add -p
git commit --amend --no-edit
\`\`\`

### Amend с изменением даты:

\`\`\`bash
# Изменить дату коммита
git commit --amend --date="2024-01-15 10:00:00"

# Использовать текущую дату
git commit --amend --reset-author
\`\`\`

### Amend для подписания коммита:

\`\`\`bash
# Подписать коммит GPG
git commit --amend -S --no-edit

# Добавить Signed-off-by
git commit --amend -s --no-edit
\`\`\`

### Множественные amend:

\`\`\`bash
# Несколько последовательных amend
git commit -m "Начало"
git add file1.txt
git commit --amend --no-edit
git add file2.txt
git commit --amend --no-edit
# В итоге один коммит с file1 и file2
\`\`\`

## Восстановление после amend

### Если случайно сделали amend:

\`\`\`bash
# Случайно изменили не тот коммит
git commit --amend -m "Неправильное сообщение"

# Восстановить исходный коммит
git reflog
# a1b2c3d HEAD@{0}: commit (amend): Неправильное сообщение
# e4f5g6h HEAD@{1}: commit: Правильное сообщение

# Вернуться к исходному
git reset --hard e4f5g6h
\`\`\`

### Если amend перезаписал изменения:

\`\`\`bash
# Добавили не те файлы в amend
git add wrong-file.txt
git commit --amend --no-edit

# Восстановить
git reflog
# Найти коммит ДО amend
git reset --hard <хеш-до-amend>

# Теперь можно сделать правильно
\`\`\`

## Типичные ошибки

### Ошибка: "You are in the middle of a merge"

\`\`\`bash
$ git commit --amend
fatal: You are in the middle of a merge -- cannot amend.
\`\`\`

**Причина**: Нельзя amend во время merge
**Решение**: Сначала завершите merge или отмените его

### Ошибка: "fatal: You are in the middle of a rebase"

\`\`\`bash
$ git commit --amend
fatal: You are in the middle of a rebase -- cannot amend.
\`\`\`

**Причина**: Нельзя amend во время rebase (обычно)
**Решение**: Используйте \`git rebase --edit-todo\` или продолжите rebase

### Ошибка: "Changes not staged for commit"

\`\`\`bash
$ git commit --amend
On branch main
Changes not staged for commit:
  modified: src/file.ts
\`\`\`

**Причина**: Amend использует только индексированные изменения
**Решение**:
\`\`\`bash
git add src/file.ts
git commit --amend --no-edit
\`\`\`

## Предупреждение о публичных коммитах

### Золотое правило:

**Не используйте amend для коммитов, которые уже отправлены в публичный репозиторий!**

### Почему?

\`\`\`bash
# На сервере:
A --- B --- C (origin/main)

# Локально сделали amend:
A --- B --- C' (локально, другой хеш)

# При push:
git push
# ! [rejected] main -> main (non-fast-forward)

# Если сделать --force:
git push --force
# Другие разработчики имеют C, вы имеете C'
# Рассинхронизация!
\`\`\`

### Безопасные альтернативы:

\`\`\`bash
# Вместо amend — новый коммит
git add forgotten-file.txt
git commit -m "Добавить забытый файл"

# Вместо amend сообщения — пустой коммит с новым сообщением
git commit --allow-empty -m "Правильное сообщение"
\`\`\`

## Связь с другими концепциями

- **git rebase -i** — изменение нескольких коммитов
- **git reset** — отмена коммита с сохранением изменений
- **git reflog** — восстановление после неудачного amend
- **git commit** — создание нового коммита
- **git push --force-with-lease** — безопасная перезапись истории
`,
      steps: [
        {
          id: 1,
          title: 'Просмотр последнего коммита',
          description: 'Посмотрите информацию о последнем коммите.',
          expectedCommand: 'git log -1',
          hint: {
            level1: 'Посмотрите последний коммит.',
            level2: 'Используйте git log -1.',
            level3: 'Введите: git log -1',
          },
        },
        {
          id: 2,
          title: 'Изменение сообщения',
          description: 'Измените сообщение последнего коммита.',
          expectedCommand: 'git commit --amend -m "Обновлённое сообщение"',
          validationRegex: 'git commit --amend -m ".*"',
          hint: {
            level1: 'Измените сообщение коммита.',
            level2: 'Используйте git commit --amend -m "сообщение".',
            level3: 'Введите: git commit --amend -m "Обновлённое сообщение"',
          },
        },
      ],
      keyInsight: {
        main: 'Amend удобен для быстрых исправлений, но используйте его только для локальных коммитов.',
        example:
          'Забыли добавить файл в коммит? git add forgotten-file && git commit --amend --no-edit. Опечатались в сообщении? git commit --amend -m "Правильное сообщение".',
        warning:
          'Amend изменяет хеш коммита! Если вы уже отправили коммит в публичный репозиторий, amend создаст рассинхронизацию. Другие разработчики получат конфликты.',
        tip: 'Если случайно сделали amend, используйте git reflog для восстановления исходного коммита. Найдите хеш до amend и выполните git reset --hard <хеш>.',
        additional: [
          '--no-edit сохраняет исходное сообщение коммита.',
          '--author позволяет изменить автора коммита.',
          'Amend создаёт новый коммит, заменяя последний.',
          'git reflog показывает историю изменений HEAD.',
          'Не используйте amend для публичных коммитов.',
        ],
      },
      estimatedTime: 15,
      quizQuestions: [
        {
          id: '4-4-q1',
          question: 'Что делает git commit --amend?',
          options: [
            {
              id: 'a',
              text: 'Создаёт новый коммит',
              isCorrect: false,
              explanation: 'Неправильно. Amend изменяет последний коммит, не создаёт новый.',
            },
            {
              id: 'b',
              text: 'Изменяет последний коммит',
              isCorrect: true,
              explanation: 'Правильно! Amend заменяет последний коммит новым с изменениями.',
            },
            {
              id: 'c',
              text: 'Удаляет последний коммит',
              isCorrect: false,
              explanation: 'Неправильно. Для удаления используйте reset.',
            },
            {
              id: 'd',
              text: 'Создаёт ветку',
              isCorrect: false,
              explanation: 'Неправильно. Для создания ветки используйте branch.',
            },
          ],
          explanation:
            'git commit --amend заменяет последний коммит новым, который включает дополнительные изменения или новое сообщение. Хеш коммита изменяется!',
          difficulty: 'easy',
        },
        {
          id: '4-4-q2',
          question: 'Как добавить забытый файл в последний коммит без изменения сообщения?',
          options: [
            {
              id: 'a',
              text: 'git add file && git commit --amend',
              isCorrect: false,
              explanation: 'Неправильно. Это откроет редактор для изменения сообщения.',
            },
            {
              id: 'b',
              text: 'git add file && git commit --amend --no-edit',
              isCorrect: true,
              explanation: 'Правильно! --no-edit сохраняет исходное сообщение.',
            },
            {
              id: 'c',
              text: 'git add file && git commit --append',
              isCorrect: false,
              explanation: 'Неправильно. Флага --append нет.',
            },
            {
              id: 'd',
              text: 'git add file && git commit --add',
              isCorrect: false,
              explanation: 'Неправильно. Флага --add нет.',
            },
          ],
          explanation:
            'git commit --amend --no-edit добавляет изменения в последний коммит, сохраняя исходное сообщение. Это удобно для добавления забытых файлов.',
          difficulty: 'easy',
        },
        {
          id: '4-4-q3',
          question: 'Почему опасно использовать amend для публичных коммитов?',
          options: [
            {
              id: 'a',
              text: 'Amend удаляет файлы',
              isCorrect: false,
              explanation: 'Неправильно. Amend не удаляет файлы.',
            },
            {
              id: 'b',
              text: 'Amend изменяет хеш коммита, создавая рассинхронизацию',
              isCorrect: true,
              explanation: 'Правильно! Другие разработчики будут иметь другой хеш.',
            },
            {
              id: 'c',
              text: 'Amend требует интернет',
              isCorrect: false,
              explanation: 'Неправильно. Amend работает локально.',
            },
            {
              id: 'd',
              text: 'Amend медленный',
              isCorrect: false,
              explanation: 'Неправильно. Скорость не проблема.',
            },
          ],
          explanation:
            'Amend создаёт новый коммит с другим хешем. Если другие разработчики уже скачали оригинальный коммит, их история расходится с вашей, что приводит к конфликтам при push/pull.',
          difficulty: 'medium',
        },
        {
          id: '4-4-q4',
          question: 'Как восстановить коммит после случайного amend?',
          options: [
            {
              id: 'a',
              text: 'git undo',
              isCorrect: false,
              explanation: 'Неправильно. Такой команды нет.',
            },
            {
              id: 'b',
              text: 'git reflog и git reset --hard <хеш>',
              isCorrect: true,
              explanation: 'Правильно! reflog показывает историю, reset восстанавливает.',
            },
            {
              id: 'c',
              text: 'git restore',
              isCorrect: false,
              explanation: 'Неправильно. restore восстанавливает файлы, не коммиты.',
            },
            {
              id: 'd',
              text: 'git revert',
              isCorrect: false,
              explanation: 'Неправильно. revert создаёт обратный коммит.',
            },
          ],
          explanation:
            'git reflog показывает историю всех изменений HEAD. Найдите хеш до amend и выполните git reset --hard <хеш> для восстановления.',
          difficulty: 'medium',
        },
        {
          id: '4-4-q5',
          question: 'Как изменить автора последнего коммита?',
          options: [
            {
              id: 'a',
              text: 'git commit --amend --author="Имя <email>"',
              isCorrect: true,
              explanation: 'Правильно! --author позволяет изменить автора.',
            },
            {
              id: 'b',
              text: 'git change-author "Имя <email>"',
              isCorrect: false,
              explanation: 'Неправильно. Такой команды нет.',
            },
            {
              id: 'c',
              text: 'git config author "Имя <email>"',
              isCorrect: false,
              explanation: 'Неправильно. config меняет настройки, не автора коммита.',
            },
            {
              id: 'd',
              text: 'git commit --change-author "Имя <email>"',
              isCorrect: false,
              explanation: 'Неправильно. Флага --change-author нет.',
            },
          ],
          explanation:
            'git commit --amend --author="Имя <email@example.com>" изменяет автора последнего коммита. Можно также использовать --reset-author для применения текущих настроек.',
          difficulty: 'medium',
        },
      ],
      resources: [
        {
          title: 'Git Documentation: git commit --amend',
          url: 'https://git-scm.com/docs/git-commit#Documentation/git-commit.txt---amend',
          type: 'documentation',
          description: 'Документация о флаге --amend',
        },
        {
          title: 'Git Reflog Documentation',
          url: 'https://git-scm.com/docs/git-reflog',
          type: 'documentation',
          description: 'Документация о reflog для восстановления коммитов',
        },
        {
          title: 'Amend and Rewriting History',
          url: 'https://www.atlassian.com/git/tutorials/rewriting-history',
          type: 'article',
          description: 'Статья о переписывании истории от Atlassian',
        },
      ],
    },
  ],
};

export { module4 };
